import type { Stage, StageColumns } from './stages';
import { STAGES, dateSeconds, frac } from './stages';

export type Highlight = { from: string; to: string };

const transitionMode = (from: Stage, to: Stage) => {
  if (from === to) return 'hold';
  const scrubbable =
    STAGES[from].log === STAGES[to].log || from === 'empty' || to === 'empty';
  return scrubbable ? 'scrub' : 'morph';
};

const maxTotal = ({ pro, per }: StageColumns[Stage]) => {
  let mx = 1e-9;
  for (let i = 0; i < pro.length; i++) mx = Math.max(mx, pro[i] + per[i]);
  return mx;
};

export const paintBars = (
  barsEl: HTMLElement,
  cols: StageColumns,
  from: Stage,
  to: Stage,
  p: number,
  highlight: Highlight | undefined
) => {
  const mode = transitionMode(from, to);
  const cur = cols[to];
  const prev = cols[from];
  const n = cur.pro.length;
  const mxCur = maxTotal(cur);
  const mxPrev = maxTotal(prev);
  // The scale follows the transformation: as the fix completes, the chart
  // re-zooms to the new stage's own max. Empty transitions keep the real
  // stage's scale (lerping toward ~zero would blow the drain up).
  const mx =
    mode !== 'scrub'
      ? mxCur
      : from === 'empty' || to === 'empty'
        ? Math.max(mxCur, mxPrev)
        : mxPrev + (mxCur - mxPrev) * p;
  const hlFrom = highlight ? frac(dateSeconds(highlight.from)) : 0;
  const hlTo = highlight ? frac(dateSeconds(highlight.to)) : 1;

  for (let i = 0; i < n; i++) {
    const col = barsEl.children[i] as HTMLElement | undefined;
    if (!col) continue;
    let proH: number;
    let perH: number;
    let removed = 0;
    let added = 0;
    if (mode === 'morph') {
      proH =
        ((prev.pro[i] / mxPrev) * (1 - p) + (cur.pro[i] / mxCur) * p) * 100;
      perH =
        ((prev.per[i] / mxPrev) * (1 - p) + (cur.per[i] / mxCur) * p) * 100;
    } else if (mode === 'scrub') {
      const curTot = cur.pro[i] + cur.per[i];
      const prevTot = prev.pro[i] + prev.per[i];
      const kept = Math.min(prevTot, curTot);
      removed = ((Math.max(0, prevTot - curTot) * (1 - p)) / mx) * 100;
      added = ((Math.max(0, curTot - prevTot) * p) / mx) * 100;
      const ratio = curTot > 0 ? cur.pro[i] / curTot : 1;
      proH = ((kept * ratio) / mx) * 100;
      perH = ((kept * (1 - ratio)) / mx) * 100;
    } else {
      proH = (cur.pro[i] / mx) * 100;
      perH = (cur.per[i] / mx) * 100;
    }
    const keptStack = proH + perH;
    const s = (col.children[0] as HTMLElement)
      .children as unknown as HTMLElement[];
    s[0].style.height = `${proH}%`;
    s[1].style.bottom = `${proH}%`;
    s[1].style.height = `${perH}%`;
    s[2].style.bottom = `${keptStack}%`;
    s[2].style.height = `${removed}%`;
    s[3].style.bottom = `${keptStack}%`;
    s[3].style.height = `${added}%`;
    col.style.opacity =
      !highlight || (i / n >= hlFrom && i / n <= hlTo) ? '1' : '0.3';
  }
};

export const paintBand = (
  bandEl: HTMLElement,
  highlight: Highlight | undefined
) => {
  if (!highlight) {
    bandEl.style.opacity = '0';
    return;
  }
  const from = frac(dateSeconds(highlight.from));
  const to = frac(dateSeconds(highlight.to));
  bandEl.style.opacity = '1';
  bandEl.style.left = `${from * 100}%`;
  bandEl.style.width = `${(to - from) * 100}%`;
};
